import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { sendBidNotification } from '../lib/notifications';

export function TestBid() {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const [existingBids, setExistingBids] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isTestMode, setIsTestMode] = useState(false);

  useEffect(() => {
    checkExistingBids();
    checkNotifications();
  }, []);

  const checkNotifications = async () => {
    try {
      const { data, error } = await supabase
        .from('email_notifications')
        .select(`
          id,
          request_id,
          recipient_email,
          sent_at,
          status,
          notification_templates (
            subject
          )
        `)
        .order('sent_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setNotifications(data || []);
    } catch (err) {
      console.error('Error checking notifications:', err);
    }
  };

  const checkExistingBids = async () => {
    try {
      const { data, error } = await supabase
        .from('bids')
        .select('*')
        .eq('request_id', '123e4567-e89b-12d3-a456-426614174000');

      if (error) throw error;
      console.log('Existing bids:', data);
      setExistingBids(data || []);
    } catch (err) {
      console.error('Error checking bids:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess('');

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    try {
      const { data, error } = await supabase.rpc('place_bid', {
        p_request_id: '123e4567-e89b-12d3-a456-426614174000', // Test UUID
        p_insurer_id: '5878700e-05be-4e58-848b-95a66655c9c5', // Known insurer ID
        p_amount: numericAmount
      });

      if (error) {
        // Handle specific error messages from the database function
        const errorMessage = error.message === 'Invalid request' ? 'Insurance request not found or expired' :
                           error.message === 'Unauthorized' ? 'You are not authorized to place bids' :
                           'Failed to place bid. Please try again.';
        setError(errorMessage);
        return;
      }

      if (isTestMode) {
        // Send test notification
        const { error: notifyError } = await sendBidNotification('123e4567-e89b-12d3-a456-426614174000');
        if (notifyError) {
          setError('Bid placed but notification failed: ' + notifyError.message);
          return;
        }
        await checkNotifications();
      }

      await checkExistingBids(); // Refresh bids after successful submission
      setSuccess(`Bid submitted successfully! Amount: £${numericAmount.toFixed(2)}`);
      setAmount('');
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Test Bid Placement</h2>
      
      {existingBids.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Existing Bids</h3>
          <div className="space-y-2 divide-y divide-gray-100">
            {existingBids.map((bid) => (
              <div key={bid.id} className="text-sm text-gray-600 py-2">
                <span className="font-medium">£{bid.amount.toFixed(2)}</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  bid.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {bid.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notification Test Toggle */}
      <div className="mb-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={isTestMode}
            onChange={(e) => setIsTestMode(e.target.checked)}
            className="rounded text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-600">Enable notification testing</span>
        </label>
      </div>

      {/* Recent Notifications */}
      {isTestMode && notifications.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Recent Notifications</h3>
          <div className="space-y-2 divide-y divide-gray-100">
            {notifications.map((notification) => (
              <div key={notification.id} className="text-sm text-gray-600 py-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{notification.notification_templates?.subject}</p>
                    <p className="text-xs text-gray-500">To: {notification.recipient_email}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    notification.status === 'sent' ? 'bg-green-100 text-green-800' :
                    notification.status === 'failed' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {notification.status}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(notification.sent_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-600 text-sm">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bid Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">£</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="block w-full pl-7 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />
          </div>
        </div>

        <button
          type="submit"
          className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {isTestMode ? 'Place Test Bid with Notification' : 'Place Test Bid'}
        </button>
      </form>
    </div>
  );
}